import { useCallback, useEffect, useRef, useState } from 'react';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import type Polygon from '@arcgis/core/geometry/Polygon';
import { useEsriMapContext } from '../EsriMapContext';
import { useFilters } from './useFilters';
import EsriUtils from '../utils/esriHelpers';

export const usePolygonDraw = (
	onPolygonComplete?: (polygon: GeoJSON.Feature<GeoJSON.Polygon>) => void,
) => {
	const { view, map, setIsDrawing } = useEsriMapContext();
	const { setPolygonFilter } = useFilters();
	const sketchRef = useRef<SketchViewModel | null>(null);
	const graphicsLayerRef = useRef<GraphicsLayer | null>(null);
	const [isActive, setIsActive] = useState(false);

	// Clean up sketch + graphics layer on unmount (e.g. grid closes mid-draw).
	useEffect(() => {
		return () => {
			sketchRef.current?.destroy();
			sketchRef.current = null;
			if (map && graphicsLayerRef.current) {
				map.remove(graphicsLayerRef.current);
				graphicsLayerRef.current = null;
			}
			setIsDrawing(false);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map]);

	const teardown = useCallback(() => {
		sketchRef.current?.destroy();
		sketchRef.current = null;
		if (map && graphicsLayerRef.current) {
			map.remove(graphicsLayerRef.current);
			graphicsLayerRef.current = null;
		}
		setIsActive(false);
		setIsDrawing(false);
	}, [map, setIsDrawing]);

	const startDrawing = useCallback(() => {
		if (!view || !map) return;

		// Temporary layer for sketch feedback — hidden from the layer panel.
		const graphicsLayer = new GraphicsLayer({ listMode: 'hide' });
		map.add(graphicsLayer);
		graphicsLayerRef.current = graphicsLayer;

		const sketch = new SketchViewModel({ view, layer: graphicsLayer });
		sketchRef.current = sketch;
		setIsActive(true);
		setIsDrawing(true);

		sketch.on('create', event => {
			if (event.state === 'complete') {
				const esriPolygon = event.graphic?.geometry as Polygon | null;
				const geoJson = EsriUtils.esriGeometryToGeoJSON(esriPolygon);
				if (geoJson?.type === 'Polygon') {
					const feature: GeoJSON.Feature<GeoJSON.Polygon> = {
						type: 'Feature',
						geometry: geoJson as GeoJSON.Polygon,
						properties: {},
					};
					setPolygonFilter(feature);
					onPolygonComplete?.(feature);
				}
				teardown();
			} else if (event.state === 'cancel') {
				teardown();
			}
		});

		sketch.create('polygon');
	}, [view, map, setPolygonFilter, onPolygonComplete, teardown, setIsDrawing]);

	const cancelDrawing = useCallback(() => {
		// SketchViewModel.cancel() fires the 'create' event with state='cancel',
		// which triggers teardown() via the listener above.
		sketchRef.current?.cancel();
	}, []);

	return { isActive, startDrawing, cancelDrawing };
};
